import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Portal, Typography } from '@mui/material';
import DealerRequiredPageShell from 'app/components/ReusableUIComponents/HighLevelComponents/DealerRequiredPageShell';
import { NotAuthorizedPage } from 'app/components/NotAuthorizedPage/Loadable';
import { EmailsGridPage } from 'app/pages/EmailsGridPage';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import HilSummaryCard from 'app/pages/HumanInTheLoopPage/components/HilSummaryCard';
import { useHumanInTheLoopPage } from 'app/pages/HumanInTheLoopPage/hooks/useHumanInTheLoopPage';
import { useHilSegmentAttentionResolve } from 'app/pages/HumanInTheLoopPage/hooks/useHilSegmentAttentionResolve';
import ResolveEntitiesModalMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
import { EmailTemplateGenerationProgressOverlay } from 'app/pages/EmailsGridPage/components/EmailTemplateGenerationProgressOverlay';
import { useEmailTemplateGenerationProgressHost } from 'app/pages/EmailsGridPage/hooks/useEmailTemplateGenerationProgressHost';
import SegmentResolveResponseModal from 'app/pages/HumanInTheLoopPage/components/SegmentResolveResponseModal';

function toUiEventLabel(eventName: string): string {
  return eventName
    .replace(/^nurturing_test_automation_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function HumanInTheLoopReview() {
  const {
    canAccess,
    hasSelectedDealer,
    selectedDealerId,
    breadcrumbsList,
    humanInTheLoopPath,
    activeSection,
    selectTemplatesSection,
    selectSegmentsSection,
    sectionHeading,
    counts,
    segmentsAttentionGridType,
    segmentsAttentionGridRefreshKey,
    refreshSegmentsAttentionGrid,
    handleSegmentsAttentionTotalDocuments,
    handleTemplatesPendingTotalDocuments,
  } = useHumanInTheLoopPage();

  const segmentResolve = useHilSegmentAttentionResolve({
    dealerId: selectedDealerId,
    canAccess: Boolean(canAccess),
    onResolveComplete: refreshSegmentsAttentionGrid,
  });

  const templateGenerationProgress = useEmailTemplateGenerationProgressHost({
    dealerId: selectedDealerId,
    enabled: Boolean(canAccess && selectedDealerId),
    onTemplateUpdate: () => {
      refreshSegmentsAttentionGrid();
      if (activeSection === 'templates_pending') {
        // Embedded EmailsGridPage will refetch via Redux lastRefreshTrigger on socket complete.
      }
    },
    progressCardTitle: 'Generating email templates...',
  });

  const [resolveDetailsOpen, setResolveDetailsOpen] = useState(false);

  const latestResolveSocketMessage = useMemo(
    () =>
      segmentResolve.automationSocketMessages.length > 0
        ? segmentResolve.automationSocketMessages[
            segmentResolve.automationSocketMessages.length - 1
          ]
        : null,
    [segmentResolve.automationSocketMessages],
  );

  useEffect(() => {
    if (segmentResolve.resolveApiResponseBody) {
      setResolveDetailsOpen(true);
    }
  }, [segmentResolve.resolveResponseVersion, segmentResolve.resolveApiResponseBody]);

  if (!canAccess) {
    return <NotAuthorizedPage />;
  }

  return (
    <DealerRequiredPageShell
      hasDealer={hasSelectedDealer}
      breadcrumbsList={breadcrumbsList}
      pageTitle="Human in the loop review"
      contentVariant="centered"
    >
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <HilSummaryCard
            title="Generated Templates Pending Approval"
            description="Review and approve generated templates"
            count={counts.templatesPending}
            selected={activeSection === 'templates_pending'}
            onClick={selectTemplatesSection}
          />
          <HilSummaryCard
            title="Segments Needing Attention"
            description="Address segment-level issues"
            count={counts.segmentsAttention}
            selected={activeSection === 'segments_attention'}
            onClick={selectSegmentsSection}
          />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {sectionHeading}
        </Typography>

        {activeSection === 'templates_pending' ? (
          <EmailsGridPage
            embedded
            hideGenerateEmailTemplateDropdown
            emailTemplatesGridPreset="hilPendingApproval"
            pageHeaderTitle="Generated Templates Pending Approval"
            onTotalDocumentsChange={handleTemplatesPendingTotalDocuments}
            emailTemplateBreadcrumbOrigin={{
              label: 'HIL',
              path: humanInTheLoopPath,
            }}
          />
        ) : segmentsAttentionGridType ? (
          <DataGridMAIQ
            key={`segments-attention-${segmentsAttentionGridRefreshKey}`}
            ref={segmentResolve.dataGridRef}
            type={segmentsAttentionGridType}
            onTotalDocumentsChange={handleSegmentsAttentionTotalDocuments}
            onCellClick={segmentResolve.handleCellClick}
          />
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select a dealer to view segments needing attention.
          </Alert>
        )}

        {segmentResolve.automationInProgress && segmentResolve.showResolveProgress && (
          <Portal>
            <Box
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1300,
                minWidth: 520,
              }}
            >
              <EmailGenerationProgressCard
                isGeneratingEmail={segmentResolve.automationInProgress}
                streamingData={segmentResolve.automationStreamingDataForCard}
                currentSection={segmentResolve.automationCurrentSectionForCard}
                progressOverride={segmentResolve.automationProgressPercentage}
                title="Resolving segment issues..."
                onOpenEmailGenerationPopup={() => setResolveDetailsOpen(true)}
              />
            </Box>
          </Portal>
        )}

        <EmailTemplateGenerationProgressOverlay host={templateGenerationProgress} />

        <ResolveEntitiesModalMAIQ
          open={segmentResolve.resolveModalOpen}
          onClose={segmentResolve.closeResolveModal}
          dealerId={selectedDealerId}
          row={segmentResolve.resolveModalRow}
          onSuccess={segmentResolve.handleResolveModalSuccess}
          isResolving={segmentResolve.resolveActionLoading}
          onResolveClick={() => {
            void segmentResolve.resolveRow(segmentResolve.resolveModalRow);
          }}
        />

        <SegmentResolveResponseModal
          open={resolveDetailsOpen}
          onClose={() => setResolveDetailsOpen(false)}
          body={segmentResolve.resolveApiResponseBody}
          showProgress={segmentResolve.showResolveProgress}
          progressCurrent={segmentResolve.automationProgressCurrent}
          progressTotal={segmentResolve.automationProgressTotal}
          progressPercentage={segmentResolve.automationProgressPercentage}
          latestEventLabel={
            latestResolveSocketMessage
              ? toUiEventLabel(latestResolveSocketMessage.eventName)
              : ''
          }
        />
      </>
    </DealerRequiredPageShell>
  );
}
